import { useMutation, useQuery } from "@tanstack/react-query"
import { Form, Input, Switch } from "antd"
import { Button, ModalWrapper } from "components"
import { LoadingIndicator } from "components/custom/Loader"
import { ProfileTemp } from "pages/dashboard/profile/component/ProfileTemp"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { AUTH_ENDPOINTS, authService } from "services/authService"
import { useAppNotificationStore, useAuthStore } from "stores"
import { useProfileStore } from "stores/profile"
import { PASSWORD_REGEX } from "utils/regex"
import EditPersonalDetails from "./component/EditPersonalDetails"

interface I2FADetails {
  twoFactorEnabled: boolean
  twoFactorSetup: boolean | null
}

import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import { VerifyPasswordPrompt } from "pages/2fa/modals/VerifyPasswordPrompt"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import { onQueryError } from "utils/utils"

const Profile: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { signOut, user } = useAuthStore()
  const [profile, setProfile] = useState(user)
  const { getProfile, isLoading } = useProfileStore()
  const { toast } = useAppNotificationStore()
  const [showEditPassword, setShowEditPassword] = useState(false)
  const [showEditPersonalDetails, setShowEditPersonalDetails] = useState(false)
  const [twoFaToggle, setTwoFaToggle] = useState(false)
  const { customizationSettings } = useCustomizationSettingsStore()
  const isEmployee = location.pathname.includes("/employee")

  const twoFaSetupPath = isEmployee
    ? "/employee/dashboard/twofa-setup"
    : "/dashboard/twofa-setup"

  const { isLoading: isProcessingPayment, mutate: process } = useMutation({
    mutationFn: (body: { oldPassword: string; newPassword: string }) =>
      isEmployee
        ? authService.employeeChangePassword(body)
        : authService.changePassword(body),
    onSuccess: () => {
      signOut()
    },
    onError: onQueryError,
  })

  const { data: PersonData, isLoading: isPersonLoading } = useQuery<ININRes>({
    queryKey: [
      isAdmin
        ? AUTH_ENDPOINTS.VIEW_PROFILE
        : AUTH_ENDPOINTS.EMPLOYEE_VIEW_PROFILE,
    ],
    queryFn: () =>
      isAdmin
        ? authService.getProfileDetails()
        : authService.getEmployeeProfileDetails(),
  })

  const { data: twoFaStatus, isLoading: gettingStatus } = useQuery<
    IBaseRes<I2FADetails>
  >({
    queryKey: [AUTH_ENDPOINTS.TWO_FA_DETAILS],
    queryFn: () => authService.get2FADetails(),
  })

  const onFinish = (values: any) => {
    process({
      oldPassword: values.current_password,
      newPassword: values.new_password,
    })
  }

  return (
    <>
      <DashboardWrapper>
        <div className=" pb-5 w-full">
          <div className="w-full flex justify-between">
            <h6 className="text-xl text-neutral500 font-bold mb-2">
              Account Preferences
            </h6>
          </div>
          <div className="flex flex-col h-full">
            <div className="lg:w-[80%] w-full bg-white h-auto border border-neutral30 relative  py-7 px-8">
              <div className=" flex items-center w-full justify-between mb-[50px]">
                <div className="flex items-center">
                  <>
                    <PersonAvatar
                      src={user?.userAvatarFileUrl}
                      alt=""
                      className=" bg-iris80 h-[72px] w-[72px] rounded-full flex   justify-center items-center text-white text-[40px]  font-bold"
                    />
                  </>

                  <div className=" ml-3">
                    <h6 className=" text-base font-bold text-neutral500 mb-1">
                      {profile?.userFullName}
                    </h6>
                  </div>
                </div>
              </div>
              <div className=" mb-[50px]">
                <h6 className="mb-2  text-neutral300 text-sm">
                  Personal Details
                </h6>
                <div className="border   border-neutral50 w-full mb-4"></div>
                <ProfileTemp
                  title="Email"
                  subtitle="Your registered email address"
                  value={profile?.userName as string}
                />
                <ProfileTemp
                  title="Phone number"
                  subtitle="Your registered phone number"
                  value={profile?.userPhone as string}
                  mask
                />
                <ProfileTemp
                  title="Birth  Date"
                  value={PersonData?.data?.date_of_birth!}
                  mask
                />
                <div className="flex w-full justify-between">
                  <div className=" w-5/12"></div>
                  <div className=" w-5/12">
                    <Button
                      title="Previous"
                      color="secondary"
                      // disabled={currentTab === 1}
                      onClick={() => setShowEditPersonalDetails(true)}
                      className=" font-bold"
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
              <div className="">
                <h6 className="mb-2  text-neutral300 text-sm">Security</h6>
                <div className="border   border-neutral50 w-full mb-4"></div>
                <ProfileTemp
                  title="Password"
                  value="*************"
                />
                <div className="flex w-full justify-between">
                  <div className=" w-5/12"></div>
                  <div className=" w-5/12">
                    <Button
                      title="Previous"
                      color="secondary"
                      // disabled={currentTab === 1}
                      className=" font-bold"
                      onClick={() => {
                        setShowEditPassword(true)
                      }}
                    >
                      Change Password
                    </Button>
                  </div>
                </div>

                <div className=" flex items-center  justify-between mt-8">
                  <div className=" w-5/12">
                    <h6 className=" text-sm font-bold text-neutral500 ">
                      Two-Factor Authentication
                    </h6>
                    <p className=" text-neutral100 mt-1 font-normal text-sm  ">
                      Adds an extra layer of security to your{" "}
                      {customizationSettings?.brand.platformName} to protect you
                      from unauthorised access and transactions.
                    </p>
                  </div>
                  <div className=" w-5/12 flex justify-start">
                    <p className=" text-neutral300 text-sm font-avenir">
                      <Switch
                        className={
                          twoFaToggle || twoFaStatus?.data?.twoFactorEnabled
                            ? "!bg-primary500"
                            : "!bg-[#ededed] "
                        }
                        checked={twoFaStatus?.data?.twoFactorEnabled}
                        loading={gettingStatus}
                        onChange={() => setTwoFaToggle(true)}
                      />
                    </p>
                  </div>
                </div>
              </div>
              {isPersonLoading && (
                <div className="z-[2] flex  items-center justify-center py-10 bg-white bg-opacity-75  bottom-0   absolute left-0 top-0 right-0  ">
                  <div>
                    <LoadingIndicator />
                  </div>
                </div>
              )}
            </div>
          </div>
          {showEditPassword && (
            <ModalWrapper
              title="Change your password"
              width={550}
              onClose={() => {
                setShowEditPassword(false)
                form.resetFields()
              }}
              open={showEditPassword}
            >
              <div className="border-t flex flex-col p-5 ">
                <div className="w-full border border-l-8 mb-4 border-l-[#F39F03] bg-[#FFF6E6] px-4 py-2 flex flex-col justify-center items-start">
                  <h6 className=" text-base text-neutral800 font-bold mb-[10px]">
                    Important Notice
                  </h6>
                  <p className=" text-subText3 text-sm">
                    You’ll have to sign in again after changing your password.
                  </p>
                </div>
                <div className="flex  w-full">
                  <Form
                    form={form}
                    name="basic"
                    layout="vertical"
                    autoComplete="off"
                    onFinish={onFinish}
                    rootClassName="w-full"
                  >
                    <Form.Item
                      label="Current password"
                      name="current_password"
                      rules={[
                        {
                          required: true,
                          message: "Please input your password!",
                        },
                      ]}
                      className="w-full"
                    >
                      <Input.Password className="w-full" />
                    </Form.Item>
                    <Form.Item
                      label="New password"
                      name="new_password"
                      rules={[
                        {
                          required: true,
                          message: "Please input your password!",
                        },
                        {
                          pattern: PASSWORD_REGEX,
                          message:
                            "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character",
                        },
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                    <Form.Item
                      label="Confirm new password"
                      name="confirm_password"
                      dependencies={["new_password"]}
                      rules={[
                        {
                          required: true,
                          message: "Please input your password!",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (
                              !value ||
                              getFieldValue("new_password") === value
                            ) {
                              return Promise.resolve()
                            }
                            return Promise.reject(
                              new Error(
                                "The new password that you entered do not match!"
                              )
                            )
                          },
                        }),
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>
                    <Form.Item
                    // wrapperCol={ { offset: 8, span: 16 } }
                    >
                      <div className="flex justify-end border-t py-2">
                        <Button
                          color="primary"
                          title="submit"
                          className="w-[60px]"
                          submitting={isProcessingPayment}
                        >
                          Submit
                        </Button>
                      </div>
                    </Form.Item>
                  </Form>
                </div>
              </div>
            </ModalWrapper>
          )}
          {showEditPersonalDetails && (
            <EditPersonalDetails
              closeModal={() => setShowEditPersonalDetails(false)}
              PersonData={PersonData?.data!}
              isAdmin={isAdmin ? true : false}
            />
          )}

          {twoFaToggle &&
            (twoFaStatus?.data?.twoFactorEnabled ? (
              // disabling — plain password check, then the setup page
              <VerifyPasswordPrompt
                onClose={(status) => {
                  setTwoFaToggle(false)
                  if (status) navigate(twoFaSetupPath)
                }}
              />
            ) : (
              // enabling — passwordauthenticate returns the setup token, which we
              // hand to the setup page through router state
              <VerifyPasswordPrompt
                onSetupToken={(setupToken) =>
                  navigate(twoFaSetupPath, { state: { setupToken } })
                }
                onClose={() => setTwoFaToggle(false)}
              />
            ))}
        </div>
      </DashboardWrapper>
    </>
  )
}

export default Profile

type ININRes = IBaseRes<ININData>
export interface ININData {
  person_key?: null
  person_id: number
  person_type_id?: null
  person_type?: null
  reports_count?: null
  peers?: null
  manager_id?: null
  manager_first_name?: null
  manager_middle_initial?: null
  manager_last_name?: null
  manager_full_name?: null
  manager_nick_name?: null
  manager_email?: null
  manager_mobile_phone?: null
  employment_status_id?: null
  employment_status?: null
  employment_status_alias?: null
  working_days?: null
  office_address_id?: null
  office_full_address?: null
  office_display_name?: null
  employee_type_id?: null
  employee_type?: null
  employee_title_id?: null
  employee_title?: null
  paygrade_id?: null
  paygrade_name?: null
  paygrade_alias?: null
  payband_id?: null
  payband_name?: null
  payband_alias?: null
  annual_gross?: null
  annual_earning?: null
  currency_symbol?: null
  department_id?: null
  department?: null
  hire_date?: null
  hire_date_short?: null
  anniversary?: null
  hire_date_day_id: number
  hire_date_month_id: number
  hire_date_month_week: number
  hire_date_week_day: number
  hire_date_calendar_date?: null
  first_name: string
  middle_initial?: null
  last_name: string
  full_name?: null
  nick_name: string
  identification_number?: null
  gender_id?: null
  gender?: null
  date_of_birth: string
  date_of_birth_short?: null
  date_of_birth_month_id: number
  date_of_birth_month_week: number
  date_of_birth_week_day: number
  date_of_birth_month_alias: string
  date_of_birth_day_id: number
  date_of_birth_day_alias: string
  date_of_birth_year: number
  date_of_birth_year_alias: string
  date_of_birth_calendar_date?: null
  email: string
  recovery_email?: null
  mobile_phone: string
  nin?: null | string
  tin?: null
  nhf_epn?: null
  pfa_name?: null
  pfa_code?: null
  rsa_pin?: null
  contractor_type_id?: null
  contractor_type?: null
  payment_method_id?: null
  payment_method?: null
  person_status_id?: null
  person_status?: null
  marital_status_id?: null
  marital_status?: null
  preferred_account_class_id?: null
  preferred_account_class?: null
  is_wallet_primary_account?: null
  is_wallet_secondary_account?: null
  custom_fields?: null
  display_phone_number?: null
  welcome_note?: null
  setup_percentage?: null
  setup_message?: null
  offer_status_id?: null
  onboarding_note_id: number
  onboarding_note?: null
  is_invited?: null
  is_confirmed?: null
  confirmation_date?: null
  compensation_effective_date?: null
  union_membership?: null
  payroll_start_date?: null
  last_compensation_update_date?: null
  cooperative?: null
  assignment_category?: null
  is_onboarded?: null
  is_onboarding_cancelled?: null
  is_new?: null
  is_on_probation?: null
  birthday_card_last_sent_date?: null
  anniversary_card_last_sent_date?: null
  welcome_card_sent?: null
  has_profile: boolean
  opted_into_nhf?: null
  opted_into_pension?: null
  voluntary_pension_contribution?: null
  avatar_url: string
  wallet_id?: null
  is_deleted?: null
  is_active?: null
  paygrade?: null
  accountDetails?: null
  officeAddress?: null
  homeAddress?: null
  nextofKin?: null
  description?: null
  employeeRequests?: null
  payables?: null
  documentTemplates?: null
  assets?: null
  employeeBenefits?: null
  employeeNotes?: null
  employeeLogs?: null
  employeePaygradeHistory?: null
  employeeHmoPlan?: null
  employeePfa?: null
  employeeDocuments?: null
  employeeAssets?: null
  employeeCustomFields?: null
  employeeCustomFieldFiles?: null
  employeeRoutineTasks?: null
  employeeOnboardingTasks?: null
  customFields?: null
  reports?: null
  employeeObjectives?: null
  employeeObjectiveItems?: null
  conversations?: null
  nextConversation?: null
  feedbacks?: null
  employeeReviews?: null
  reviewEntities?: null
  filteredEmployeeRequests?: null
  payrollItems?: null
  payrollItems_YearToDate?: null
  customBenefitPayrollItems?: null
  lastPayslip?: null
  splitPaymentConfigs?: null
  next_pay_date?: null
  next_pay_date_short?: null
  is_two_factor_enabled: boolean
  is_google_authenticator_enabled?: null
  authenticator: ININDataAuthenticator
  currentDate?: null
  checkPaymentOptionEnabled?: null
  twoFactorEnabled: boolean
  leaveRequests: number
  loanRequests: number
  salaryAdvanceRequests: number
  documentRequests: number
  exitRequests: number
  redeploymentRequests: number
  suspensions: number
  payrollChanges: number
  payrolls: number
  customPaymentSchedules: number
  violations: number
  avatarUrl?: null
  twoFactorRequired?: null
  logoUploaded?: null
  pensionPlaceholder?: null
  objectiveCount: number
  offTrackObjectiveCount: number
  actionCount: number
  notCompletedActionCount: number
  conversationCount: number
  reviewCount: number
  feedbackRequestCount: number
  hasProfile: boolean
  employeeCount: number
  selectedYear: number
  currentYear: number
  employeeProfileUpdatesEnabled?: null
  twoFactorAuthenticationEnabled: boolean
  isDefaultOrganisation: boolean
  nextRegularPayrollCutOffDate?: null
  nextRegularPayrollCutOffDate_Short?: null
  nextRegularPayrollCutOffMonth?: null
  nextRegularPayrollCutOffDay?: null
  nextOffCyclePayrollCutOffDate?: null
  nextOffCyclePayrollCutOffDate_Short?: null
  nextOffCyclePayrollCutOffMonth?: null
  nextOffCyclePayrollCutOffDay?: null
  customFieldsEnabled?: null
  multipleAdminsEnabled?: null
  admins: number
  customFieldCount: number
  setupPercentage?: null
  autoSendBirthdayCard?: null
  autoSendAnniversaryCard?: null
  splitPaymentEnabled?: null
  globalPreferredAccountClassId?: null
}
export interface ININDataAuthenticator {
  secret_key: string
  barcode_url: string
}

export interface I2faData {
  twoFactorEnabled: boolean
  twoFactorSetup: boolean
}
